import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/hen-ui/utils';
import { gridVariants, type GridVariantProps } from './grid.variants';

export interface GridProps
  extends React.HTMLAttributes<HTMLDivElement>,
    GridVariantProps {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
}

/**
 * Grid - Responsive multi-column grid layout component
 *
 * Replaces manual `<div class="grid grid-cols-* gap-*">` patterns.
 * Use Grid for multi-column layouts like card grids and feature sections.
 *
 * @example Basic usage
 * ```tsx
 * <Grid columns={1} columns-md={2} columns-lg={4} gap="lg">
 *   <ImageCard ... />
 *   <ImageCard ... />
 *   <ImageCard ... />
 *   <ImageCard ... />
 * </Grid>
 * ```
 *
 * @example Simple two-column grid
 * ```tsx
 * <Grid columns={2} gap="md">
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 * </Grid>
 * ```
 *
 * @example Responsive card grid
 * ```tsx
 * <Grid columns={1} columns-md={2} columns-lg={3} gap="xl">
 *   {cards.map(card => <Card key={card.id} {...card} />)}
 * </Grid>
 * ```
 */
export const Grid = React.forwardRef<HTMLDivElement, GridProps>(
  (
    {
      className,
      columns = 1,
      'columns-md': columnsMd,
      'columns-lg': columnsLg,
      gap = 'lg',
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp
        className={cn(
          gridVariants({
            columns,
            'columns-md': columnsMd,
            'columns-lg': columnsLg,
            gap,
          }),
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

Grid.displayName = 'Grid';
